import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faUpload, faFileExcel, faSpinner, faCheckCircle, 
  faExclamationTriangle, faDownload, faInfoCircle 
} from '@fortawesome/free-solid-svg-icons';
import { labelService, LabelImportResult } from '../../services/labelService';

interface LabelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (result: LabelImportResult) => void;
}

export const LabelImportModal: React.FC<LabelImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<LabelImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validierung
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type)) {
        setError('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls)');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB
        setError('Die Datei ist zu groß. Maximale Größe: 10MB');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setError(null);
      
      const result = await labelService.importFromExcel(selectedFile);
      setUploadResult(result);
      onImportComplete(result);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    setIsUploading(false);
    onClose();
  };

  const downloadTemplate = () => {
    // Erstelle eine Beispiel-CSV für den Download
    const headers = [
      '_Value_DE',
      '#WEDLabelIconPrint',
      '#WEDLLabelIconWebList', 
      '#WEDLLabelIconWebDetail',
      '#RelGHPFreigabe'
    ];
    
    const exampleData = [
      'Bio-Produkt',
      'https://example.com/bio-print.png',
      'https://example.com/bio-list.png',
      'https://example.com/bio-detail.png',
      'true'
    ];
    
    const csvContent = [
      headers.join(','),
      exampleData.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'label-import-template.csv';
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faFileExcel} className="text-green-600 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900">Label Import</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Erwartete Excel-Struktur</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><strong>_Value_DE:</strong> Label-Name (Pflichtfeld)</li>
                  <li><strong>#WEDLabelIconPrint:</strong> URL zum Druck-Icon</li>
                  <li><strong>#WEDLLabelIconWebList:</strong> URL zum Web-Listen-Icon</li>
                  <li><strong>#WEDLLabelIconWebDetail:</strong> URL zum Web-Detail-Icon</li>
                  <li><strong>#RelGHPFreigabe:</strong> GHP-Freigabe (true/false)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Template Download */}
          <div className="flex justify-center">
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} />
              Beispiel-Template herunterladen
            </button>
          </div>

          {/* File Upload */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Excel-Datei auswählen
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isUploading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <FontAwesomeIcon icon={faUpload} className="text-4xl text-gray-400" />
                <div>
                  <p className="text-lg font-medium text-gray-700">
                    Klicken Sie hier oder ziehen Sie eine Datei hierher
                  </p>
                  <p className="text-sm text-gray-500">
                    Unterstützte Formate: .xlsx, .xls (Max. 10MB)
                  </p>
                </div>
              </label>
            </div>

            {selectedFile && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faFileExcel} className="text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">{selectedFile.name}</p>
                    <p className="text-sm text-green-700">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-2">Import erfolgreich!</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-700">Neu importiert:</span>
                      <span className="font-semibold ml-2">{uploadResult.imported}</span>
                    </div>
                    <div>
                      <span className="text-green-700">Aktualisiert:</span>
                      <span className="font-semibold ml-2">{uploadResult.updated}</span>
                    </div>
                    <div>
                      <span className="text-green-700">Duplikate:</span>
                      <span className="font-semibold ml-2">{uploadResult.duplicates}</span>
                    </div>
                    <div>
                      <span className="text-green-700">Fehler:</span>
                      <span className="font-semibold ml-2">{uploadResult.errors}</span>
                    </div>
                  </div>
                  
                  {uploadResult.details.errorMessages.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-red-700 mb-1">Fehler-Details:</p>
                      <div className="bg-white border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                        {uploadResult.details.errorMessages.map((error, index) => (
                          <p key={index} className="text-xs text-red-600">{error}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isUploading}
            >
              {uploadResult ? 'Schließen' : 'Abbrechen'}
            </button>
            
            {!uploadResult && (
              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isUploading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                {isUploading ? 'Importiere...' : 'Labels importieren'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
